// src/store/slices/centralAuthSlice.ts
// Central Identity authentication state management.
//
// Thin wrapper: creates the core slice from @levelup/central-auth factory,
// and adds app-specific state (isAuthenticating, isSwitchingWorkspace)
// plus app-scoped selectors.
//
// IMPORTANT: Token Storage Security
// - Access token: MEMORY ONLY (Redux state, not persisted to localStorage)
// - Refresh: Handled via Central session cookie (credentials:'include')
// - On page refresh, user must re-authenticate via Central session.

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createCentralAuthSlice, parseJwtWorkspaceId } from '@levelup/central-auth';
import type { CentralUser, CentralWorkspace } from '@levelup/central-auth';
import { STORAGE_KEY_PREFIX } from '../../config/centralAuth';

// Re-export shared types for backward compatibility
export type { CentralUser, CentralWorkspace };

// ============================================================
// Create core slice from shared factory
// ============================================================

// The shared factory defaults `storage` to localStorage. We override with an
// in-memory shim so access/refresh tokens live only in Redux state for the
// lifetime of the tab — matching the "MEMORY ONLY" guarantee documented at
// the top of this file. Without this shim a token persists across reloads
// and a stale (expired) token gets replayed against switch-context, which
// Central rejects with 401 → CentralUnavailableOverlay (the bug we hit on
// 2026-05-11 with a token issued 2026-04-14).
const memoryStorage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> = (() => {
  const store = new Map<string, string>();
  return {
    getItem: (key) => (store.has(key) ? store.get(key) ?? null : null),
    setItem: (key, value) => { store.set(key, value); },
    removeItem: (key) => { store.delete(key); },
  };
})();

const {
  reducer: coreReducer,
  actions: coreActions,
  storageKeys,
} = createCentralAuthSlice({
  storageKeyPrefix: STORAGE_KEY_PREFIX,
  storage: memoryStorage,
});

// Re-export storage keys for manual cleanup if needed
export { storageKeys as CENTRAL_STORAGE_KEYS };

// ============================================================
// App-specific extended state
// ============================================================

/**
 * App-specific state that extends the shared auth state.
 * These fields are unique to BeHeard's UI flow.
 */
interface BeHeardAuthExtension {
  /** Whether OIDC flow is in progress */
  isAuthenticating: boolean;
  /** Whether a workspace switch is in progress (show blocking overlay) */
  isSwitchingWorkspace: boolean;
}

const extensionInitialState: BeHeardAuthExtension = {
  isAuthenticating: false,
  isSwitchingWorkspace: false,
};

/**
 * App-specific slice for BeHeard-only auth UI state.
 * Core auth state lives in the shared centralAuth slice.
 */
const beheardAuthExtSlice = createSlice({
  name: 'beheardAuthExt',
  initialState: extensionInitialState,
  reducers: {
    setAuthenticating: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticating = action.payload;
    },
    setSwitchingWorkspace: (state, action: PayloadAction<boolean>) => {
      state.isSwitchingWorkspace = action.payload;
    },
  },
});

// ============================================================
// Core action exports (from shared factory)
// ============================================================

export const {
  setCentralCredentials,
  updateCentralAccessToken,
  updateCentralUser,
  updateCentralUserAvatar,
  syncCentralUserFromBoot,
  selectCentralWorkspace,
  centralLogout,
  setCentralInitialized,
} = coreActions;

// ============================================================
// App-specific action exports
// ============================================================

export const {
  setAuthenticating,
  setSwitchingWorkspace,
} = beheardAuthExtSlice.actions;

// ============================================================
// Reducers
// ============================================================

export default coreReducer;
export const beheardAuthExtReducer = beheardAuthExtSlice.reducer;

// ============================================================
// State type (for selectors)
// ============================================================

interface CentralAuthState {
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
  user: CentralUser | null;
  selectedWorkspace: CentralWorkspace | null;
  isInitialized: boolean;
  isAuthenticated: boolean;
}

// ============================================================
// Selectors — core auth state
// ============================================================

export const selectCentralUser = (state: { centralAuth: CentralAuthState }) =>
  state.centralAuth.user;

export const selectCentralAccessToken = (state: { centralAuth: CentralAuthState }) =>
  state.centralAuth.accessToken;

export const selectCentralRefreshToken = (state: { centralAuth: CentralAuthState }) =>
  state.centralAuth.refreshToken;

export const selectCurrentCentralWorkspace = (state: { centralAuth: CentralAuthState }) =>
  state.centralAuth.selectedWorkspace;

export const selectCentralWorkspaces = (state: { centralAuth: CentralAuthState }) =>
  state.centralAuth.user?.workspaces ?? [];

export const selectIsCentralAuthenticated = (state: { centralAuth: CentralAuthState }) =>
  state.centralAuth.isAuthenticated;

export const selectIsCentralInitialized = (state: { centralAuth: CentralAuthState }) =>
  state.centralAuth.isInitialized;

export const selectCentralTokenExpiresAt = (state: { centralAuth: CentralAuthState }) =>
  state.centralAuth.expiresAt;

/**
 * Check if Central token needs refresh (expires within 5 minutes).
 * In BeHeard's OIDC mode, this triggers re-authentication via session cookie.
 */
export const selectCentralNeedsRefresh = (state: { centralAuth: CentralAuthState }) => {
  const { expiresAt, isAuthenticated } = state.centralAuth;
  if (!expiresAt || !isAuthenticated) return false;
  return Date.now() > expiresAt - 5 * 60 * 1000;
};

/**
 * For backward compatibility - always false in OIDC mode.
 */
export const selectCanRefresh = (_state: { centralAuth: CentralAuthState }) => false;

/**
 * Extract workspaceId from the Central JWT token.
 */
export const selectTokenWorkspaceId = (state: { centralAuth: CentralAuthState }): string | null => {
  return parseJwtWorkspaceId(state.centralAuth.accessToken);
};

// ============================================================
// Selectors — app-specific extension state
// ============================================================

export const selectIsAuthenticating = (state: { beheardAuthExt: BeHeardAuthExtension }) =>
  state.beheardAuthExt.isAuthenticating;

export const selectIsSwitchingWorkspace = (state: { beheardAuthExt: BeHeardAuthExtension }) =>
  state.beheardAuthExt.isSwitchingWorkspace;

// ============================================================
// Backward-compatible action: markRefreshFailed
// In OIDC mode, this clears the token to trigger re-auth.
// ============================================================

export { centralLogout as markRefreshFailed };

// ============================================================
// Backward-compatible re-exports from bootContextSlice
// These let existing consumers continue importing boot-related
// actions/selectors from centralAuthSlice without changing imports.
// New code should import from bootContextSlice directly.
// ============================================================

export {
  // Selectors (backward-compatible — read from bootContext state)
  selectBootCompleted,
  selectWorkspaceClosed,
  selectCentralUnavailable,
  selectOtherWorkspaces,
  selectEntitlementsEffective,
  selectSuiteAppsRaw,
  selectSuiteAppsNormalized,
  selectSwitchContextMeta,
  // Actions (re-exported with shared payload shapes)
  setBootReady,
  setBootWorkspaceClosed,
  setBootUnavailable,
  resetBoot,
} from './bootContextSlice';
